import { ICardListGroupConfig } from '~/client/src/shared/components/CardList/types'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { UNASSIGNED_FILTER_VALUE } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import {
  getLbsBucketAncestors,
  GroupKey,
  isProjectScopedMapView,
} from './applyMapViewsTransform'
import { IMapViewItem, MapViewCapability } from './MapViewsSidebarListItem'

// Scope keys carry an explicit prefix so a stale `collapsedKeys` entry outliving
// a group-mode switch can't collide with LBS paths or capability discriminators.
const SCOPE_PREFIX = 'scope:'
const PROJECT_SCOPE_BUCKET_KEY = `${SCOPE_PREFIX}${UNASSIGNED_FILTER_VALUE}`

export type IMapViewsHeaderMeta =
  | {
      headerKind: 'lbs'
      scopeIconName: MapViewLocationIcon
      scopeColor?: string
    }
  | {
      headerKind: 'scope'
      scopeIconName: MapViewLocationIcon
      scopeColor?: string
    }
  | {
      headerKind: 'type'
      capability: MapViewCapability
    }

export interface IMapViewsGroupingResult {
  /** Undefined when group mode is 'none'. */
  groupConfig: ICardListGroupConfig<IMapViewItem> | undefined
  headerMetaByKey: Map<string, IMapViewsHeaderMeta>
}

const capabilityLabel = (capability: MapViewCapability): string =>
  capability === 'georeferenced'
    ? Localization.translator.georeferenced
    : Localization.translator.whiteboard

const lbsBucketKey = (item: IMapViewItem): string =>
  item.lbsAncestry.length === 0
    ? ''
    : '/' + item.lbsAncestry.map(l => l.id).join('/')

function buildLbsGrouping(
  items: readonly IMapViewItem[],
): IMapViewsGroupingResult {
  const projectLabel = Localization.translator.project
  const labelByKey = new Map<string, string>([['', projectLabel]])
  const headerMetaByKey = new Map<string, IMapViewsHeaderMeta>([
    [
      '',
      {
        headerKind: 'lbs',
        scopeIconName: MapViewLocationIcon.Project,
      },
    ],
  ])

  // First-seen wins — LBS levels are stable within a project.
  for (const item of items) {
    let path = ''
    for (const level of item.lbsAncestry) {
      path += `/${level.id}`
      if (!labelByKey.has(path)) {
        labelByKey.set(path, level.label)
        headerMetaByKey.set(path, {
          headerKind: 'lbs',
          scopeIconName: level.iconName,
          scopeColor: level.color,
        })
      }
    }
  }

  const groupConfig: ICardListGroupConfig<IMapViewItem> = {
    groupBy: lbsBucketKey,
    getGroupLabel: key => labelByKey.get(key) ?? '',
    getGroupAncestors: getLbsBucketAncestors,
    // Lexicographic on slash-paths gives DFS order: '' < '/a' < '/a/b' < '/a/b/c' < '/b'.
    sortGroups: (a, b) => a.localeCompare(b),
  }

  return { groupConfig, headerMetaByKey }
}

function buildScopeGrouping(
  items: readonly IMapViewItem[],
): IMapViewsGroupingResult {
  // Project bucket identified via the data-model predicate, not a translated-label compare —
  // locale switches and non-localised adapter scopeLabels would break a string compare.
  const bucketKey = (item: IMapViewItem): string =>
    isProjectScopedMapView(item)
      ? PROJECT_SCOPE_BUCKET_KEY
      : `${SCOPE_PREFIX}${item.scopeLabel}`

  const labelByKey = new Map<string, string>()
  const headerMetaByKey = new Map<string, IMapViewsHeaderMeta>()

  for (const item of items) {
    const key = bucketKey(item)
    if (!labelByKey.has(key)) {
      labelByKey.set(
        key,
        key === PROJECT_SCOPE_BUCKET_KEY
          ? Localization.translator.project
          : item.scopeLabel,
      )
      headerMetaByKey.set(key, {
        headerKind: 'scope',
        scopeIconName: item.scopeIconName,
        scopeColor: item.scopeColor,
      })
    }
  }

  const groupConfig: ICardListGroupConfig<IMapViewItem> = {
    groupBy: bucketKey,
    getGroupLabel: key => labelByKey.get(key) ?? '',
    sortGroups: (a, b) =>
      (labelByKey.get(a) ?? '').localeCompare(
        labelByKey.get(b) ?? '',
        undefined,
        { numeric: true, sensitivity: 'base' },
      ),
  }

  return { groupConfig, headerMetaByKey }
}

function buildTypeGrouping(): IMapViewsGroupingResult {
  const headerMetaByKey = new Map<string, IMapViewsHeaderMeta>([
    ['georeferenced', { headerKind: 'type', capability: 'georeferenced' }],
    ['whiteboard', { headerKind: 'type', capability: 'whiteboard' }],
  ])

  const groupConfig: ICardListGroupConfig<IMapViewItem> = {
    groupBy: item => item.capability,
    getGroupLabel: key => capabilityLabel(key as MapViewCapability),
    // Encounter-order via stable sort — first item's capability defines the leading band.
    sortGroups: () => 0,
  }

  return { groupConfig, headerMetaByKey }
}

/** 'none' → groupConfig undefined (flat); 'lbs' → tree via getGroupAncestors; others flat. */
export function buildMapViewsGroupConfig(
  items: readonly IMapViewItem[],
  group: GroupKey,
): IMapViewsGroupingResult {
  switch (group) {
    case 'none':
      return { groupConfig: undefined, headerMetaByKey: new Map() }
    case 'lbs':
      return buildLbsGrouping(items)
    case 'scope':
      return buildScopeGrouping(items)
    case 'type':
      return buildTypeGrouping()
  }
}
